import { Component, Inject, OnDestroy, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { FORMATS_DASH_NUMBER } from '../../shared/utils/format-data-picker';
import { Logger, LoggerFactory } from '../../core/logger.service';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AudienceFilterInputType, AudienceFilterOperator } from '../../audience/components/audience.model';
import { WidgetSimpleInfo } from '../../campaign/widgets.model';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AccountService } from '../../account/account.service';
import { TranslateService } from '@ngx-translate/core';
import {
  ChoosableOptionGroup,
  SessionCondition,
  SessionConditionCampaignEventConfig,
  SessionConditionConfig,
  SessionConditionType,
} from '../../audience/components/session-condition-group.model';
import SessionConditionHelpers from '../../audience/session-condition-helpers';
import {
  applyComparison,
  comparisonLabelKey,
  comparisonOptionsFor,
  isValuelessType,
  minimumValueFor,
  SessionConditionComparison,
  SessionConditionComparisonOption,
  toComparison,
} from '../../audience/session-condition-comparison';
import { FilterProperty } from '../../search/filter-property';
import { LoadSuggestionsService } from '../../search/load-suggestions.service';
import { Subscription } from 'rxjs';
import { IntegrationType } from 'app/automations/integrations-models';
import { IntegrationsService } from 'app/shared/services/integrations.service';

@Component({
  selector: 'app-session-condition-dialog',
  templateUrl: './session-condition-dialog.component.html',
  styleUrls: ['./session-condition-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'sv_SE' },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_DATE_FORMATS, useValue: FORMATS_DASH_NUMBER },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SessionConditionDialogComponent implements OnDestroy {
  public readonly LOGGER: Logger;
  public updateInProgress = false;
  public updateFormGroup: UntypedFormGroup;

  public dialogTitle: string;
  public isNewCondition = true;
  public sessionCondition: SessionCondition;
  public FilterInputTypes = AudienceFilterInputType;
  public SessionConditionTypes = SessionConditionType;
  public CampaignSessionConditionTypes = [
    SessionConditionType.CampaignOpened,
    SessionConditionType.CampaignClickthrough,
    SessionConditionType.CampaignConversion,
    SessionConditionType.CampaignDismissal,
  ];
  public showOperator = true;
  public filterInputType: AudienceFilterInputType;

  public conditionOperator: AudienceFilterOperator;
  public comparisonOptions: SessionConditionComparisonOption[] = [];
  public conditionComparisonLabelKey: string;
  public minimumValue: number | undefined;
  public leadInKey: string;
  public operatorFirst = false;
  private conditionComparison: SessionConditionComparison;
  public filterValueString: string;
  public filterValueNumber: number;
  public filterValueArray: Array<any>;
  public conditionValueError = false;
  public valueBelowMinimumError = false;
  public domainHasPath = false;
  public audienceOperatorError = false;
  public placeholderKey: string;

  public choosableValueOptions: Array<{ key: any; value: any }>;
  public choosableValueGroups: ChoosableOptionGroup[] = [];
  public chosenOptionNames = '';
  public allWidgets: Array<WidgetSimpleInfo>;
  public selectedCampaignId: number | null = null;
  public loadingOptions = false;
  public accountDomain: string;
  private getAccountSub: Subscription;
  private subscriptionArray: Subscription[] = [];

  public SessionConditionCampaignEventOperators = [
    AudienceFilterOperator.Equals,
    AudienceFilterOperator.MoreThan,
    AudienceFilterOperator.LessThan,
  ];
  public campaignEventOperator: AudienceFilterOperator;
  public campaignEventCountSupported = false;
  public campaignEventCount: number;
  public campaignEventCountError: string;
  public campaignEventCountFC = new UntypedFormControl(1, [Validators.required, Validators.min(0), Validators.max(10)]);

  public get isCampaignCondition(): boolean {
    return (
      this.sessionCondition?.type === SessionConditionType.CampaignOpened ||
      this.sessionCondition?.type === SessionConditionType.CampaignClickthrough ||
      this.sessionCondition?.type === SessionConditionType.CampaignConversion ||
      this.sessionCondition?.type === SessionConditionType.CampaignDismissal ||
      this.sessionCondition?.type === SessionConditionType.CampaignSurveyResponse
    );
  }

  public onCampaignSelected(id: number | null): void {
    this.selectedCampaignId = id;
    this.filterValueString = id != null ? '' + id : undefined;
  }

  constructor(
    private dialogRef: MatDialogRef<SessionConditionDialogComponent>,
    private readonly accountService: AccountService,
    protected readonly loggerFactory: LoggerFactory,
    private readonly translateService: TranslateService,
    private readonly loadSuggestionsService: LoadSuggestionsService,
    private readonly integrationsService: IntegrationsService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      sessionCondition: SessionCondition;
      newConditionConfig: SessionConditionConfig;
      accountDomain: string;
      allWidgets: Array<WidgetSimpleInfo>;
    },
  ) {
    this.LOGGER = loggerFactory.get('SessionConditionDialogComponent');
    this.isNewCondition = !!data.sessionCondition;
    this.allWidgets = data.allWidgets;
    this.accountDomain = data.accountDomain;
    this.initializeSessionCondition(
      data.sessionCondition ? JSON.parse(JSON.stringify(data.sessionCondition)) : undefined,
      data.newConditionConfig,
    );
  }

  public filterOperatorUpdated(operator: AudienceFilterOperator) {
    this.conditionOperator = operator;
    this.filterInputType = SessionConditionHelpers.getInputTypeForConditionAndOperator(
      this.sessionCondition.type,
      this.conditionOperator,
    );
  }

  public updateChosenOptionNames(): void {
    this.chosenOptionNames = SessionConditionHelpers.chosenOptionNames(
      this.choosableValueOptions ?? [],
      this.filterValueArray ?? [],
    );
  }

  public comparisonUpdated(comparison: SessionConditionComparison): void {
    this.setComparison(comparison);
    this.refreshValueInput(applyComparison(this.sessionCondition, comparison));
  }

  public campaignEventOperatorUpdated(operator: AudienceFilterOperator) {
    this.campaignEventOperator = operator;
  }

  public filterValueStringChanged(value: string): void {
    this.filterValueString = value;
    this.updateDomainHasPath();
  }

  public saveCondition(): void {
    let skipTranslatingValue = false;

    if (this.CampaignSessionConditionTypes.indexOf(this.sessionCondition.type) !== -1) {
      if (this.campaignEventCountFC.invalid) {
        this.campaignEventCountError = 'audience.timeframe.invalid-times';
        return;
      }
      if (this.filterValueString === undefined) {
        this.conditionValueError = true;
        return;
      }
      const campaignEventBody = {
        campaignId: +this.filterValueString,
        times: +this.campaignEventCountFC.value,
      } as SessionConditionCampaignEventConfig;
      this.campaignEventCountError = undefined;
      this.conditionValueError = false;
      this.sessionCondition.value = JSON.stringify(campaignEventBody);
      this.sessionCondition.operator = this.campaignEventOperator;
      this.dialogRef.close({ sessionCondition: this.sessionCondition, skipTranslatingValue: false });
      return;
    }

    if (this.conditionComparison && !this.filterInputType) {
      this.dialogRef.close({
        sessionCondition: applyComparison(this.sessionCondition, this.conditionComparison),
        skipTranslatingValue: false,
      });
      return;
    }

    if (AudienceFilterInputType.MultiChoiceDropdown === this.filterInputType) {
      this.sessionCondition.value = this.filterValueArray.join(',');
    } else if (AudienceFilterInputType.Number === this.filterInputType) {
      this.sessionCondition.value = Number.isFinite(this.filterValueNumber) ? '' + this.filterValueNumber : undefined;
    } else if (this.sessionCondition.type === SessionConditionType.Domain) {
      this.filterValueStringChanged(SessionConditionHelpers.cleanDomainValue(this.filterValueString));
      this.sessionCondition.value = this.filterValueString;
    } else {
      this.sessionCondition.value = this.filterValueString;
    }
    if (!this.sessionCondition.value || this.sessionCondition.value === '') {
      this.conditionValueError = true;
      return;
    }
    this.conditionValueError = false;
    if (this.conditionComparison) {
      this.valueBelowMinimumError = this.minimumValue !== undefined && +this.sessionCondition.value < this.minimumValue;
      if (this.valueBelowMinimumError) {
        return;
      }
      this.sessionCondition = applyComparison(this.sessionCondition, this.conditionComparison);
    } else {
      this.sessionCondition.operator = this.conditionOperator;
      if (!this.sessionCondition.operator) {
        this.audienceOperatorError = true;
        return;
      }
    }
    if (this.choosableValueOptions) {
      const foundOption = this.choosableValueOptions.find((o) => o.key === this.sessionCondition.value);
      if (foundOption) {
        if (!this.sessionCondition.conditionTranslations) {
          this.sessionCondition.conditionTranslations = {};
        }
        this.sessionCondition.conditionTranslations.value = foundOption.value;
        skipTranslatingValue = true;
      }
    }

    this.conditionValueError = false;
    this.dialogRef.close({ sessionCondition: this.sessionCondition, skipTranslatingValue: skipTranslatingValue });
  }

  private initializeSessionCondition(existingCondition: SessionCondition, newConditionConfig: SessionConditionConfig) {
    if (existingCondition) {
      this.sessionCondition = existingCondition;
    } else if (newConditionConfig) {
      this.sessionCondition = SessionConditionHelpers.conditionConfigToCondition(newConditionConfig);
    } else {
      throw new Error('Existing condition should be sent to dialog or a type for a new filter');
    }
    this.getAccountSub = this.accountService.getCurrentAccount().subscribe(() => {
      this.loadChoosableValueOptions();
    });
    this.comparisonOptions = comparisonOptionsFor(this.sessionCondition.type);
    const comparison = toComparison(this.sessionCondition);
    if (comparison) {
      this.setComparison(comparison);
    }
    const config = this.sessionCondition.conditionConfig;
    this.operatorFirst = isValuelessType(this.sessionCondition.type);
    this.leadInKey = this.operatorFirst ? config?.titleKey : (config?.leadInKey ?? config?.descriptionKey);
    this.showOperator = !comparison && this.shouldOperatorBeDisplayed(this.sessionCondition);
    this.conditionOperator = this.sessionCondition.operator;
    this.refreshValueInput(this.sessionCondition);

    this.parseConditionValueToCorrectType();
    this.updateChosenOptionNames();
    this.updateDomainHasPath();
  }

  private loadChoosableValueOptions() {
    this.choosableValueGroups = [];
    if (this.sessionCondition.type === SessionConditionType.UTMCampaign) {
      this.loadUtmCampaignListToOptions();
    } else if (this.sessionCondition.type === SessionConditionType.BrowserLanguage) {
      this.loadBrowserLanguages();
    } else if (this.sessionCondition.type === SessionConditionType.OperatingSystem) {
      this.choosableValueOptions = SessionConditionHelpers.getOperatingSystems();
    } else if (this.sessionCondition.type === SessionConditionType.Browser) {
      this.choosableValueOptions = SessionConditionHelpers.getBrowsers(this.translateService);
      this.choosableValueGroups = SessionConditionHelpers.groupChoosableOptions(this.choosableValueOptions);
      this.updateChosenOptionNames();
    } else if (
      this.sessionCondition.type === SessionConditionType.CampaignOpened ||
      this.sessionCondition.type === SessionConditionType.CampaignClickthrough ||
      this.sessionCondition.type === SessionConditionType.CampaignConversion ||
      this.sessionCondition.type === SessionConditionType.CampaignDismissal ||
      this.sessionCondition.type === SessionConditionType.CampaignSurveyResponse
    ) {
      this.loadAllCampaignNames();
    } else if (this.sessionCondition.type === SessionConditionType.IdentifiedEmailMarketing) {
      this.integrationsService.getQueryStrings().subscribe((result) => {
        this.choosableValueOptions = Object.keys(result).map((x) => ({ key: x, value: x }));
      });
    }
  }

  public loadAllCampaignNames(): void {
    this.loadingOptions = true;
    this.choosableValueOptions = SessionConditionHelpers.widgetsToCampaignsAsChoosableOptions(
      this.allWidgets,
      this.translateService,
    );
    if (this.filterValueString) {
      this.selectedCampaignId = +this.filterValueString;
    }
    this.loadingOptions = false;
  }

  private loadBrowserLanguages(): void {
    this.loadingOptions = true;
    this.choosableValueOptions = SessionConditionHelpers.getBrowserLanguages();
    this.loadingOptions = false;
  }

  private loadUtmCampaignListToOptions(): void {
    this.loadingOptions = true;
    this.loadSuggestionsService.getCampaignsAsync().then((campaigns: Array<FilterProperty>) => {
      const choosableValueOptions = [] as Array<{ key: any; value: any }>;
      if (campaigns) {
        campaigns.forEach((campaign: FilterProperty) => {
          choosableValueOptions.push({ key: campaign.id, value: campaign.displayName });
        });
      }
      this.choosableValueOptions = choosableValueOptions;
      this.loadingOptions = false;
    });
  }

  private refreshValueInput(condition: SessionCondition): void {
    this.filterInputType = SessionConditionHelpers.getInputTypeForCondition(condition);
    this.placeholderKey = SessionConditionHelpers.getPlaceholderKeyForConditionAndOperator(
      this.sessionCondition.conditionConfig,
      condition.type,
      SessionConditionHelpers.getValueInputOperator(condition),
      this.accountDomain,
      this.translateService,
    );
  }

  private setComparison(comparison: SessionConditionComparison): void {
    this.conditionComparison = comparison;
    this.conditionComparisonLabelKey = comparisonLabelKey(comparison);
    this.minimumValue = minimumValueFor(comparison);
    this.valueBelowMinimumError = false;
  }

  private updateDomainHasPath(): void {
    this.domainHasPath =
      this.sessionCondition.type === SessionConditionType.Domain &&
      SessionConditionHelpers.domainHasPath(this.filterValueString);
  }

  private shouldOperatorBeDisplayed(sessionCondition: SessionCondition): boolean {
    if (
      !sessionCondition.conditionConfig.supportedOperators ||
      sessionCondition.conditionConfig.supportedOperators.length < 2
    ) {
      return false;
    }
    return true;
  }

  private parseCampaignEventConditionValue(sessionCondition: SessionCondition) {
    if (isNaN(+sessionCondition.value)) {
      const parsed = JSON.parse(sessionCondition.value) as SessionConditionCampaignEventConfig;
      if (parsed.campaignId === undefined) {
        this.filterValueString = undefined;
      } else {
        this.filterValueString = '' + parsed.campaignId;
      }
      this.campaignEventCount = parsed.times;
    } else {
      this.filterValueString = '' + sessionCondition.value;
      this.campaignEventCount = 1;
    }
    this.campaignEventCountFC.patchValue(this.campaignEventCount);
    this.campaignEventOperator = sessionCondition.operator;
  }

  private parseConditionValueToCorrectType() {
    if (this.CampaignSessionConditionTypes.indexOf(this.sessionCondition.type) !== -1) {
      this.parseCampaignEventConditionValue(this.sessionCondition);
    } else if (this.filterInputType === AudienceFilterInputType.MultiChoiceDropdown) {
      if (this.sessionCondition.value) {
        this.filterValueArray = this.sessionCondition.value.replace(/ /g, '').split(',');
      } else {
        this.filterValueArray = [];
      }
    } else if (this.filterInputType === AudienceFilterInputType.Number) {
      this.filterValueNumber = Number(this.sessionCondition.value);
    } else {
      this.filterValueString = this.sessionCondition.value;
    }
  }

  public closeDialog(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.subscriptionArray.push(this.getAccountSub);
    this.subscriptionArray.forEach((subs) => subs && subs.unsubscribe());
  }
}
